import { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getVerticals, getProblems, createProblem, updateProblem, deleteProblem, getSolutionByProblem, createSolution, updateSolution, aiRewrite, downloadTemplate, bulkImportProblems, exportProblems, exportVerticalsProblemsExcel } from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, Eye, Video, Wand2, RefreshCw, Upload, Download, FileUp, FileText, CheckCircle, AlertCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { getIcon } from "@/lib/icons";

const getYouTubeVideoId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : '';
};

export default function AdminProblems() {
  const [verticals, setVerticals] = useState([]);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [solutionDialogOpen, setSolutionDialogOpen] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [solution, setSolution] = useState(null);
  const [formData, setFormData] = useState({
    vertical_id: "", title: "", description: "", challenge: "", icon: "AlertCircle", target_audience: ["", "", ""]
  });
  const [solutionForm, setSolutionForm] = useState({
    overview: "",
    steps: [],
    value_delivered: {},
    youtube_urls: ["", "", "", "", ""],
    youtube_titles: ["", "", "", "", ""]
  });
  const [saving, setSaving] = useState(false);
  const [solutionTab, setSolutionTab] = useState("overview");
  // AI Rewriter state
  const [rewriteField, setRewriteField] = useState(null);
  const [rewriteStyle, setRewriteStyle] = useState("humanized");
  const [isRewriting, setIsRewriting] = useState(false);
  const [rewritePreview, setRewritePreview] = useState("");
  
  // Bulk Import States
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [verticalsRes, problemsRes] = await Promise.all([getVerticals(), getProblems()]);
      setVerticals(verticalsRes.data);
      setProblems(problemsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  
  // Bulk Import Functions
  const handleDownloadTemplate = async (templateType) => {
    try {
      const res = await downloadTemplate(templateType);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', templateType === 'problem' ? 'PROBLEM_STATEMENT_TEMPLATE.json' : 'PROBLEM_EXAMPLE_PHARMA.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  const handleExportProblems = async () => {
    try {
      const res = await exportProblems();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `problems_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Problems exported!');
    } catch (error) {
      toast.error('Failed to export problems');
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await exportVerticalsProblemsExcel();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Absolin_Verticals_Problems_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully!');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a JSON file');
      return;
    }
    
    setUploading(true);
    setImportResult(null);
    
    try {
      const res = await bulkImportProblems(file);
      setImportResult(res.data);
      if (res.data.success) {
        toast.success(`Import successful! Created: ${res.data.created}, Updated: ${res.data.updated}`);
        fetchData();
      } else {
        toast.warning(`Import completed with errors. Created: ${res.data.created}, Updated: ${res.data.updated}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({ success: false, message: error.response?.data?.detail || 'Import failed', errors: [error.message] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleOpenDialog = (problem = null) => {
    if (problem) {
      setEditingProblem(problem);
      setFormData({
        vertical_id: problem.vertical_id,
        title: problem.title,
        description: problem.description,
        challenge: problem.challenge,
        icon: problem.icon || "AlertCircle",
        target_audience: problem.target_audience || ["", "", ""]
      });
    } else {
      setEditingProblem(null);
      setFormData({ vertical_id: "", title: "", description: "", challenge: "", icon: "AlertCircle", target_audience: ["", "", ""] });
    }
    setDialogOpen(true);
  };

  const handleOpenSolutionDialog = async (problem) => {
    setSelectedProblem(problem);
    setSolutionTab("overview");
    try {
      const res = await getSolutionByProblem(problem.id);
      if (res.data) {
        setSolution(res.data);
        const existingUrls = res.data.youtube_urls || [];
        const existingTitles = res.data.youtube_titles || [];
        const urls = ["", "", "", "", ""];
        const titles = ["", "", "", "", ""];
        existingUrls.forEach((url, i) => { if (i < 5) urls[i] = url; });
        existingTitles.forEach((title, i) => { if (i < 5) titles[i] = title; });
        
        setSolutionForm({
          overview: res.data.overview,
          steps: res.data.steps || [],
          value_delivered: res.data.value_delivered || {},
          youtube_urls: urls,
          youtube_titles: titles
        });
      } else {
        setSolution(null);
        setSolutionForm({
          overview: "",
          steps: [
            { step_number: 1, title: "", description: "", duration: "", icon: "Search" },
            { step_number: 2, title: "", description: "", duration: "", icon: "PenTool" },
            { step_number: 3, title: "", description: "", duration: "", icon: "Code" }
          ],
          value_delivered: {},
          youtube_urls: ["", "", "", "", ""],
          youtube_titles: ["", "", "", "", ""]
        });
      }
      setSolutionDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load solution");
    }
  };

  const handleSave = async () => {
    if (!formData.vertical_id || !formData.title || !formData.description || !formData.challenge) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      const saveData = {
        ...formData,
        target_audience: (formData.target_audience || []).filter(a => a.trim())
      };
      if (editingProblem) {
        await updateProblem(editingProblem.id, saveData);
        toast.success("Problem updated");
      } else {
        await createProblem(saveData);
        toast.success("Problem created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save problem");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSolution = async () => {
    if (!solutionForm.overview) {
      toast.error("Please provide solution overview");
      return;
    }
    setSaving(true);
    try {
      const filteredVideos = solutionForm.youtube_urls
        .map((url, i) => ({ url: url?.trim() || "", title: solutionForm.youtube_titles[i]?.trim() || "" }))
        .filter(v => v.url);
      
      const solutionData = {
        problem_id: selectedProblem.id,
        overview: solutionForm.overview,
        steps: solutionForm.steps.filter(s => s.title),
        value_delivered: Object.fromEntries(Object.entries(solutionForm.value_delivered).filter(([_, v]) => v)),
        youtube_urls: filteredVideos.map(v => v.url),
        youtube_titles: filteredVideos.map(v => v.title)
      };

      if (solution) {
        await updateSolution(solution.id, solutionData);
        toast.success("Solution updated");
      } else {
        await createSolution(solutionData);
        toast.success("Solution created");
      }
      setSolutionDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save solution");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this problem and its solution?")) return;
    try {
      await deleteProblem(id);
      toast.success("Problem deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // AI Rewriter functions
  const handleRewrite = async (field, text) => {
    if (!text) {
      toast.error("No text to rewrite");
      return;
    }
    setRewriteField(field);
    setIsRewriting(true);
    setRewritePreview("");
    try {
      const res = await aiRewrite(text, rewriteStyle, "en");
      setRewritePreview(res.data.rewritten);
    } catch (error) {
      toast.error("AI rewrite failed");
    } finally {
      setIsRewriting(false);
    }
  };

  const applyRewrite = () => {
    if (!rewriteField || !rewritePreview) return;
    
    if (rewriteField === "title") {
      setFormData({ ...formData, title: rewritePreview });
    } else if (rewriteField === "description") {
      setFormData({ ...formData, description: rewritePreview });
    } else if (rewriteField === "challenge") {
      setFormData({ ...formData, challenge: rewritePreview });
    } else if (rewriteField === "overview") {
      setSolutionForm({ ...solutionForm, overview: rewritePreview });
    } else if (rewriteField.startsWith("step_")) {
      const index = parseInt(rewriteField.split("_")[1]);
      const newSteps = [...solutionForm.steps];
      newSteps[index] = { ...newSteps[index], description: rewritePreview };
      setSolutionForm({ ...solutionForm, steps: newSteps });
    }
    
    setRewriteField(null);
    setRewritePreview("");
    toast.success("Applied rewritten text");
  };

  // Step management
  const addStep = () => {
    setSolutionForm({
      ...solutionForm,
      steps: [...solutionForm.steps, { step_number: solutionForm.steps.length + 1, title: "", description: "", duration: "", icon: "CheckCircle" }]
    });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...solutionForm.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSolutionForm({ ...solutionForm, steps: newSteps });
  };

  const removeStep = (index) => {
    const newSteps = solutionForm.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, step_number: i + 1 }));
    setSolutionForm({ ...solutionForm, steps: newSteps });
  };

  const getVerticalName = (id) => {
    const v = verticals.find(v => v.id === id);
    return v ? v.name : "Unknown";
  };

  // AI Rewriter Component - Language dropdown removed per user request
  const AIRewriterTool = ({ field, text, onRewrite }) => (
    <div className="flex items-center gap-2 mt-1">
      <Select value={rewriteStyle} onValueChange={setRewriteStyle}>
        <SelectTrigger className="w-28 h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="humanized">Humanized</SelectItem>
          <SelectItem value="professional">Professional</SelectItem>
          <SelectItem value="simple">Simple</SelectItem>
          <SelectItem value="engaging">Engaging</SelectItem>
        </SelectContent>
      </Select>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onRewrite(field, text)}
        disabled={isRewriting && rewriteField === field}
        className="h-8 gap-1"
      >
        {isRewriting && rewriteField === field ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Wand2 className="h-3 w-3" />
        )}
        AI Rewrite
      </Button>
    </div>
  );

  return (
    <AdminLayout title="Problems & Solutions">
      <div className="space-y-6" data-testid="admin-problems">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-slate-600">Manage problem statements and solutions</p>
            <p className="text-xs text-amber-600 mt-1">Note: Pricing, Before/After, Deliverables, ROI, FAQ are now managed in Verticals</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setBulkImportDialogOpen(true)} data-testid="bulk-import-problems-btn">
              <Upload className="h-4 w-4 mr-2" /> Bulk Import
            </Button>
            <Button variant="outline" onClick={handleExportProblems} data-testid="export-problems-btn">
              <Download className="h-4 w-4 mr-2" /> Export JSON
            </Button>
            <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn" className="border-green-300 text-green-700 hover:bg-green-50">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-problem-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Problem
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Vertical</TableHead>
                  <TableHead className="hidden lg:table-cell">Challenge</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></TableCell></TableRow>
                ) : problems.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">No problems found</TableCell></TableRow>
                ) : (
                  problems.map((problem) => (
                    <TableRow key={problem.id}>
                      <TableCell className="font-medium">{problem.title}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="px-2 py-1 rounded-full bg-[#E6F0FA] text-[#0066CC] text-xs">{getVerticalName(problem.vertical_id)}</span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-500 max-w-xs truncate">{problem.challenge}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenSolutionDialog(problem)} title="Edit Solution"><Eye className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(problem)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(problem.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Problem Dialog with AI Rewriter */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingProblem ? "Edit Problem" : "Add New Problem"}
                <span className="text-xs text-slate-500 font-normal flex items-center gap-1 ml-2">
                  <Wand2 className="h-3 w-3" /> AI Rewriter available
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Vertical *</Label>
                <Select value={formData.vertical_id} onValueChange={(v) => setFormData({ ...formData, vertical_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select vertical" /></SelectTrigger>
                  <SelectContent>{verticals.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Inventory Mismanagement" />
                <AIRewriterTool field="title" text={formData.title} onRewrite={handleRewrite} />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Describe the problem" rows={3} />
                <AIRewriterTool field="description" text={formData.description} onRewrite={handleRewrite} />
              </div>
              <div className="space-y-2">
                <Label>Challenge *</Label>
                <Textarea value={formData.challenge} onChange={(e) => setFormData({ ...formData, challenge: e.target.value })} placeholder="e.g., Manual tracking leads to 15-20% inventory discrepancies" rows={2} />
                <AIRewriterTool field="challenge" text={formData.challenge} onRewrite={handleRewrite} />
              </div>

              {/* Target Audience */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Who should explore this solution?</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => setFormData({ ...formData, target_audience: [...(formData.target_audience || []), ""] })}
                  >
                    <Plus className="h-3 w-3" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {(formData.target_audience || []).map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        value={item}
                        onChange={(e) => {
                          const updated = [...formData.target_audience];
                          updated[idx] = e.target.value;
                          setFormData({ ...formData, target_audience: updated });
                        }}
                        placeholder={`e.g., ${idx === 0 ? "Retail businesses struggling with deposit management" : idx === 1 ? "Operations managers seeking process efficiency" : "Decision makers evaluating ERP solutions"}`}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0 text-red-500 hover:bg-red-50"
                        onClick={() => {
                          const updated = formData.target_audience.filter((_, i) => i !== idx);
                          setFormData({ ...formData, target_audience: updated });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                {(!formData.target_audience || formData.target_audience.length === 0) && (
                  <p className="text-xs text-slate-400">No audience items. Click Add to define who should explore this solution.</p>
                )}
              </div>
              
              {/* Rewrite Preview */}
              {rewritePreview && (rewriteField === "title" || rewriteField === "description" || rewriteField === "challenge") && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-blue-700 flex items-center gap-1"><Wand2 className="h-4 w-4" /> AI Suggestion</Label>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setRewritePreview("")}>Cancel</Button>
                        <Button size="sm" onClick={applyRewrite} className="bg-[#0066CC]">Apply</Button>
                      </div>
                    </div>
                    <p className="text-slate-700 text-sm whitespace-pre-wrap">{rewritePreview}</p>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingProblem ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Simplified Solution Dialog */}
        <Dialog open={solutionDialogOpen} onOpenChange={setSolutionDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{solution ? "Edit" : "Add"} Solution: {selectedProblem?.title}</DialogTitle>
            </DialogHeader>
            
            <Tabs value={solutionTab} onValueChange={setSolutionTab} className="py-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="steps">Steps</TabsTrigger>
                <TabsTrigger value="videos"><Video className="h-4 w-4 mr-1" />Videos</TabsTrigger>
              </TabsList>
              
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Solution Overview *</Label>
                  <Textarea value={solutionForm.overview} onChange={(e) => setSolutionForm({ ...solutionForm, overview: e.target.value })} placeholder="Describe the overall solution approach" rows={4} />
                  <AIRewriterTool field="overview" text={solutionForm.overview} onRewrite={handleRewrite} />
                  
                  {rewritePreview && rewriteField === "overview" && (
                    <Card className="bg-blue-50 border-blue-200 mt-2">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-blue-700 flex items-center gap-1"><Wand2 className="h-4 w-4" /> AI Suggestion</Label>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setRewritePreview("")}>Cancel</Button>
                            <Button size="sm" onClick={applyRewrite} className="bg-[#0066CC]">Apply</Button>
                          </div>
                        </div>
                        <p className="text-slate-700 text-sm whitespace-pre-wrap">{rewritePreview}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries({ time_saving: "Time Saving", cost_reduction: "Cost Reduction", efficiency_gain: "Efficiency Gain", error_reduction: "Error Reduction", roi: "ROI" }).map(([key, label]) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-sm">{label}</Label>
                      <Input value={solutionForm.value_delivered[key] || ""} onChange={(e) => setSolutionForm({ ...solutionForm, value_delivered: { ...solutionForm.value_delivered, [key]: e.target.value } })} placeholder={`e.g., ${key === 'roi' ? '300% ROI' : '40% reduction'}`} />
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Steps Tab */}
              <TabsContent value="steps" className="space-y-4 pt-4">
                {solutionForm.steps.map((step, index) => (
                  <Card key={`step-${step.step_number || index}`}>
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-slate-500">Step {step.step_number}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeStep(index)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Title</Label><Input value={step.title} onChange={(e) => updateStep(index, 'title', e.target.value)} placeholder="Step title" /></div>
                        <div><Label className="text-xs">Duration</Label><Input value={step.duration} onChange={(e) => updateStep(index, 'duration', e.target.value)} placeholder="e.g., 2-3 weeks" /></div>
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Textarea value={step.description} onChange={(e) => updateStep(index, 'description', e.target.value)} placeholder="Step description" rows={2} />
                        <AIRewriterTool field={`step_${index}`} text={step.description} onRewrite={handleRewrite} />
                        
                        {rewritePreview && rewriteField === `step_${index}` && (
                          <Card className="bg-blue-50 border-blue-200 mt-2">
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-blue-700 text-xs flex items-center gap-1"><Wand2 className="h-3 w-3" /> AI Suggestion</Label>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRewritePreview("")}>Cancel</Button>
                                  <Button size="sm" className="h-7 text-xs bg-[#0066CC]" onClick={applyRewrite}>Apply</Button>
                                </div>
                              </div>
                              <p className="text-slate-700 text-xs whitespace-pre-wrap">{rewritePreview}</p>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button variant="outline" onClick={addStep} className="w-full"><Plus className="h-4 w-4 mr-2" />Add Step</Button>
              </TabsContent>

              {/* Videos Tab */}
              <TabsContent value="videos" className="space-y-4 pt-4">
                <Label className="flex items-center gap-2"><Video className="h-4 w-4 text-red-500" />YouTube Video URLs (up to 5)</Label>
                <div className="space-y-3">
                  {solutionForm.youtube_urls.map((url, index) => (
                    <Card key={index} className="p-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input value={solutionForm.youtube_titles[index] || ""} onChange={(e) => { const t = [...solutionForm.youtube_titles]; t[index] = e.target.value; setSolutionForm({ ...solutionForm, youtube_titles: t }); }} placeholder={`Video ${index + 1} Title`} />
                        <Input value={url} onChange={(e) => { const u = [...solutionForm.youtube_urls]; u[index] = e.target.value; setSolutionForm({ ...solutionForm, youtube_urls: u }); }} placeholder="YouTube URL" />
                      </div>
                      {url && getYouTubeVideoId(url) && (
                        <div className="mt-2 flex items-center gap-2">
                          <img src={`https://img.youtube.com/vi/${getYouTubeVideoId(url)}/mqdefault.jpg`} alt="Preview" className="w-24 h-14 object-cover rounded" />
                          <span className="text-xs text-green-600">✓ Valid</span>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSolutionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveSolution} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Solution
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={bulkImportDialogOpen} onOpenChange={setBulkImportDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="bulk-import-problems-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" /> Bulk Import Problems
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Step 1: Download Template */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-base">
                  <span className="w-6 h-6 rounded-full bg-[#0066CC] text-white text-sm flex items-center justify-center">1</span>
                  Download Template
                </Label>
                <p className="text-sm text-slate-500 ml-8">Download a template file to fill with your data</p>
                <div className="flex gap-2 ml-8">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('problem')} data-testid="download-problem-template-btn">
                    <Download className="h-4 w-4 mr-2" /> Empty Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('problem_example')} data-testid="download-problem-example-btn">
                    <FileText className="h-4 w-4 mr-2" /> Example (Pharma)
                  </Button>
                </div>
              </div>

              {/* Step 2: Fill & Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-base">
                  <span className="w-6 h-6 rounded-full bg-[#0066CC] text-white text-sm flex items-center justify-center">2</span>
                  Fill & Upload JSON
                </Label>
                <p className="text-sm text-slate-500 ml-8">Edit the template with your data and upload it back</p>
                <div className="ml-8">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="bulk-import-problems-file"
                    data-testid="bulk-import-problems-file-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full border-dashed border-2 py-8 hover:border-[#0066CC] hover:bg-blue-50"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Importing...
                      </>
                    ) : (
                      <>
                        <FileUp className="h-5 w-5 mr-2" /> Click to select JSON file
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Import Result */}
              {importResult && (
                <div className={`ml-8 p-4 rounded-lg border ${importResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-start gap-2">
                    {importResult.success ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${importResult.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                        {importResult.message}
                      </p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span className="text-emerald-600">Created: {importResult.created}</span>
                        <span className="text-blue-600">Updated: {importResult.updated}</span>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">
                          <p className="font-medium">Errors:</p>
                          <ul className="list-disc list-inside">
                            {importResult.errors.slice(0, 5).map((err, i) => (
                              <li key={`err-${i}-${err.slice(0, 20)}`}>{err}</li>
                            ))}
                            {importResult.errors.length > 5 && <li>... and {importResult.errors.length - 5} more</li>}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="ml-8 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-1">Tips:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Problems are matched by <strong>title + vertical</strong> - existing ones will be updated</li>
                  <li>Use <code className="bg-slate-200 px-1 rounded">vertical_name</code> instead of UUID if easier</li>
                  <li>Solutions can be included in the same file with the problem</li>
                  <li>You can import multiple problems using <code className="bg-slate-200 px-1 rounded">&quot;problems&quot;: [...]</code></li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkImportDialogOpen(false); setImportResult(null); }}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
